import Joi from 'joi';
import moment from 'moment';
import countries from '../../Lib/Constants/countries';

const StrippedFields = {
  style_name: Joi.optional().strip(),
  style_color: Joi.optional().strip(),
  requested_date: Joi.optional().strip(),
  vendor_id: Joi.optional().strip(),
  last_updated_date: Joi.optional().strip(),
  created_at: Joi.optional().strip(),
  submitted_by: Joi.optional().strip(),
  updated_at: Joi.optional().strip(),
  notified_at: Joi.optional().strip(),
  baseline_cost_jr: Joi.optional().strip(),
  baseline_cost_plus: Joi.optional().strip(),
  product_id: Joi.optional().strip(),
  vendor_name: Joi.optional().strip(),
  requested_for: Joi.optional().strip(),
  last_message: Joi.optional().strip(),
  photos: Joi.optional().strip(),
};

export const FieldsValidationSchema: Record<string, Joi.Schema> = {
  earliest_custom_cut_delivery_date: Joi.date()
    .min(moment().format('YYYY-MM-DD'))
    .max('9999-12-31')
    .required()
    .label('Earliest Custom Cut Delivery Date')
    .messages({
      'date.min':
        '"Earliest Custom Cut Delivery Date" cannot be earlier than today',
      'date.max':
        '"Earliest Custom Cut Delivery Date" cannot be later than 9999-12-31',
    }),
  custom_cut_lead_time_in_weeks: Joi.number()
    .integer()
    .positive()
    .max(50)
    .required()
    .label('Custom Cut Lead Time in Weeks')
    .messages({
      'number.base': '"Custom Cut Lead Time in Weeks" must be a number',
      'number.integer': '"Custom Cut Lead Time in Weeks" must be an integer',
      'number.positive':
        '"Custom Cut Lead Time in Weeks" must be a positive number',
      'number.max':
        '"Custom Cut Lead Time in Weeks" must be less than or equal to 50',
      'any.required': '"Custom Cut Lead Time in Weeks" is required',
    }),
  country_of_origin: Joi.string()
    .valid(...countries.map((country) => country.text))
    .required()
    .label('Country of Origin')
    .messages({
      'any.only': 'Country of Origin must be a valid country',
    }),
  cost_jr: Joi.number()
    .precision(2)
    .positive()
    .required()
    .label('Cost Jr')
    .messages({
      'number.positive': '"Cost Jr" must be greater than 0',
    }),
  cost_plus: Joi.number()
    .precision(2)
    .positive()
    .required()
    .label('Cost Plus')
    .messages({
      'number.positive': '"Cost Plus" must be greater than 0',
    }),
  shipment_mode: Joi.valid('Sea', 'Land', 'Air')
    .required()
    .label('Shipment Mode'),
  exclusivity: Joi.valid(
    'FN Exclusive',
    'Not Exclusive',
    'Exclusive Going Forward',
  )
    .allow(null)
    .allow('')
    .label('Fashion Nova Exclusive')
    .messages({
      'any.only':
        '"Fashion Nova Exclusive" must be Exclusive, Not Exclusive, or Exclusive Going Forward',
    }),
  stock_quantity: Joi.number()
    .integer()
    .allow(null)
    .allow('')
    .label('Stock Quantity'),
  stock_ratio: Joi.string().allow(null).allow('').label('Stock Ratio'),
  earliest_IHD_date_to_deliver_stock: Joi.date()
    .min(moment().format('YYYY-MM-DD'))
    .max('9999-12-31')
    .allow(null)
    .allow('')
    .label('Earliest IHD Date to Deliver Stock')
    .messages({
      'date.min':
        '"Earliest IHD Date to Deliver Stock" cannot be earlier than today',
      'date.max':
        '"Earliest IHD Date to Deliver Stock" cannot be later than 9999-12-31',
    }),
  incoming_stock_quantity: Joi.number()
    .integer()
    .allow(null)
    .allow('')
    .label('Incoming Stock Quantity'),
  incoming_stock_date: Joi.date()
    .min(moment().format('YYYY-MM-DD'))
    .max('9999-12-31')
    .allow(null)
    .allow('')
    .label('Incoming Stock Date')
    .messages({
      'date.min': '"Incoming Stock Date" cannot be earlier than today',
      'date.max': '"Incoming Stock Date" cannot be later than 9999-12-31',
    }),
  incoming_stock_ratio: Joi.string()
    .allow(null)
    .allow('')
    .label('Incoming Stock Ratio'),
  custom_MOQ_jr: Joi.number()
    .integer()
    .allow(null)
    .allow('')
    .label('Custom MOQ Jr'),
  custom_MOQ_plus: Joi.number()
    .integer()
    .allow(null)
    .allow('')
    .label('Custom MOQ Plus'),
  comments: Joi.string().allow(null).allow('').label('Comments'),

  ...StrippedFields,
};

export const AddCommentCommentSchema = Joi.string()
  .required()
  .label('Comments');

export const AddCommentSchema = Joi.object({
  worksheet_id: Joi.string().required(),
  comments: AddCommentCommentSchema,
});

export const UpdateOneSchema = Joi.object({
  ...FieldsValidationSchema,
  worksheet_id: Joi.optional().strip(),
});

export const UpdateBulkSchema = Joi.object({
  ...FieldsValidationSchema,
  worksheet_id: Joi.string().required(),
});
